import { z } from 'zod';
import {
  CATEGORIAS_MONOTRIBUTO, CODIGOS_JURISDICCION, CONDICIONES_IVA, ESTADOS_DELEGACION,
  REGIMENES_IIBB, ROLES, ROLES_ASIGNACION, SERVICIOS_ARCA, TIPOS_PERSONA, TIPOS_TRABAJO_ARCA, valores,
} from './catalogos.ts';
import { cuitValido, normalizarCuit, tipoPersonaPorCuit } from './cuit.ts';

// Los formularios mandan '' en los campos vacíos: se guardan como null.
const vacioANull = (v: unknown) => (typeof v === 'string' && v.trim() === '' ? null : v);
const textoOpcional = (max: number) =>
  z.preprocess(vacioANull, z.string().trim().max(max).nullish()).transform((v) => v ?? null);
const fechaOpcional = z
  .preprocess(vacioANull, z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Fecha con formato AAAA-MM-DD').nullish())
  .transform((v) => v ?? null);
const jurisdiccion = z.number().int().refine((n) => CODIGOS_JURISDICCION.includes(n as never), 'Jurisdicción inexistente');

export const esquemaCuit = z
  .string()
  .transform(normalizarCuit)
  .refine((v) => /^\d{11}$/.test(v), 'El CUIT tiene 11 dígitos')
  .refine(cuitValido, 'CUIT inválido: revisá el dígito verificador');

export const esquemaPassword = z
  .string()
  .min(12, 'Mínimo 12 caracteres')
  .max(200)
  .refine((p) => /[a-zA-Z]/.test(p) && /\d/.test(p), 'Tiene que tener letras y números');

// ---------------------------------------------------------------- Clientes

export const esquemaJurisdiccionCliente = z.object({
  jurisdiccion,
  numeroInscripcion: textoOpcional(30),
  desde: fechaOpcional,
});

export const esquemaDelegacion = z.object({
  // A qué usuario del estudio se delegó. null: delegación cargada antes de
  // que existieran los representantes; se conserva pero el robot no la usa.
  usuarioId: z.number().int().positive().nullable().default(null),
  servicio: z.enum(valores(SERVICIOS_ARCA)),
  estado: z.enum(valores(ESTADOS_DELEGACION)),
  observaciones: textoOpcional(255),
});

export const esquemaCliente = z
  .object({
    cuit: esquemaCuit,
    razonSocial: z.string().trim().min(2, 'Completá la razón social').max(200),
    nombreFantasia: textoOpcional(200),
    tipoPersona: z.enum(valores(TIPOS_PERSONA)),
    formaJuridica: textoOpcional(60),
    condicionIva: z.enum(valores(CONDICIONES_IVA)),
    monotributoCategoria: z.preprocess(vacioANull, z.enum(CATEGORIAS_MONOTRIBUTO).nullish()).transform((v) => v ?? null),
    iibbRegimen: z.enum(valores(REGIMENES_IIBB)),
    iibbNumero: textoOpcional(30),
    iibbJurisdiccionSede: z.preprocess(vacioANull, jurisdiccion.nullish()).transform((v) => v ?? null),
    empleador: z.boolean().default(false),
    fechaInicioActividades: fechaOpcional,
    cierreEjercicioMes: z.preprocess(vacioANull, z.number().int().min(1).max(12).nullish()).transform((v) => v ?? null),
    actividadPrincipalCodigo: z
      .preprocess(vacioANull, z.string().regex(/^\d{6}$/, 'Código de actividad de 6 dígitos (nomenclador F.883)').nullish())
      .transform((v) => v ?? null),
    actividadPrincipalDescripcion: textoOpcional(255),
    domicilioFiscal: textoOpcional(255),
    localidad: textoOpcional(120),
    provincia: z.preprocess(vacioANull, jurisdiccion.nullish()).transform((v) => v ?? null),
    codigoPostal: textoOpcional(10),
    email: z.preprocess(vacioANull, z.string().trim().email('Email inválido').max(190).nullish()).transform((v) => v ?? null),
    telefono: textoOpcional(60),
    contactoNombre: textoOpcional(120),
    observaciones: textoOpcional(5000),
    jurisdicciones: z.array(esquemaJurisdiccionCliente).max(24).default([]),
    delegaciones: z.array(esquemaDelegacion).max(50).default([]),
  })
  .superRefine((c, ctx) => {
    const tipo = tipoPersonaPorCuit(c.cuit);
    if (tipo && tipo !== c.tipoPersona) {
      ctx.addIssue({
        code: 'custom',
        path: ['tipoPersona'],
        message: tipo === 'juridica'
          ? 'Un CUIT que empieza con 30, 33 o 34 es de persona jurídica'
          : 'Ese prefijo de CUIT es de persona humana',
      });
    }
    if (c.condicionIva === 'monotributo' && !c.monotributoCategoria) {
      ctx.addIssue({ code: 'custom', path: ['monotributoCategoria'], message: 'Indicá la categoría de Monotributo' });
    }
    if (c.iibbRegimen === 'convenio_multilateral' && !c.iibbJurisdiccionSede) {
      ctx.addIssue({ code: 'custom', path: ['iibbJurisdiccionSede'], message: 'En Convenio Multilateral indicá la jurisdicción sede' });
    }
    const vistas = new Set<number>();
    c.jurisdicciones.forEach((j, i) => {
      if (vistas.has(j.jurisdiccion)) {
        ctx.addIssue({ code: 'custom', path: ['jurisdicciones', i, 'jurisdiccion'], message: 'Jurisdicción repetida' });
      }
      vistas.add(j.jurisdiccion);
    });
    const servicios = new Set<string>();
    c.delegaciones.forEach((d, i) => {
      const clave = `${d.usuarioId ?? '-'}:${d.servicio}`;
      if (servicios.has(clave)) {
        ctx.addIssue({ code: 'custom', path: ['delegaciones', i, 'servicio'], message: 'Servicio repetido para el mismo usuario' });
      }
      servicios.add(clave);
    });
  })
  // La categoría solo tiene sentido en Monotributo.
  .transform((c) => (c.condicionIva === 'monotributo' ? c : { ...c, monotributoCategoria: null }));

export type ClienteEntrada = z.input<typeof esquemaCliente>;
export type ClienteValidado = z.output<typeof esquemaCliente>;

export const esquemaFiltroClientes = z.object({
  q: z.string().trim().max(100).optional(),
  estado: z.enum(['activo', 'inactivo', 'todos']).default('activo'),
  condicionIva: z.enum(valores(CONDICIONES_IVA)).optional(),
  usuarioId: z.coerce.number().int().positive().optional(),
  pagina: z.coerce.number().int().min(1).default(1),
  porPagina: z.coerce.number().int().min(1).max(200).default(50),
});

// ---------------------------------------------------------------- Usuarios

export const esquemaUsuarioNuevo = z.object({
  email: z.string().trim().toLowerCase().email('Email inválido').max(190),
  nombre: z.string().trim().min(2, 'Completá el nombre').max(120),
  rol: z.enum(valores(ROLES)),
  // Sin contraseña local, el usuario solo puede entrar con Microsoft.
  passwordLocal: z.boolean().default(true),
});

export const esquemaUsuarioEdicion = z.object({
  email: z.string().trim().toLowerCase().email('Email inválido').max(190),
  nombre: z.string().trim().min(2, 'Completá el nombre').max(120),
  rol: z.enum(valores(ROLES)),
  activo: z.boolean(),
});

export const esquemaAsignacionesDeUsuario = z.object({
  asignaciones: z
    .array(z.object({ clienteId: z.number().int().positive(), rol: z.enum(valores(ROLES_ASIGNACION)) }))
    .max(5000)
    .refine((a) => new Set(a.map((x) => x.clienteId)).size === a.length, 'Cliente repetido'),
});

export const esquemaAsignacionesDeCliente = z.object({
  asignaciones: z
    .array(z.object({ usuarioId: z.number().int().positive(), rol: z.enum(valores(ROLES_ASIGNACION)) }))
    .max(500)
    .refine((a) => new Set(a.map((x) => x.usuarioId)).size === a.length, 'Usuario repetido'),
});

// ---------------------------------------------------------------- ARCA

const mes = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Mes con formato AAAA-MM');

/** Pedido de descarga de ARCA para un cliente: meses `desde`–`hasta` (AAAA-MM), inclusive. */
export const esquemaEncolarArca = z
  .object({
    tipo: z.enum(valores(TIPOS_TRABAJO_ARCA)),
    desde: mes.optional(),
    hasta: mes.optional(),
  })
  .superRefine((v, ctx) => {
    const periodo = TIPOS_TRABAJO_ARCA.find((t) => t.valor === v.tipo)!.periodo;
    if (periodo === 'ninguno') return;
    if (!v.desde) ctx.addIssue({ code: 'custom', path: ['desde'], message: 'Indicá desde qué mes' });
    if (!v.hasta) ctx.addIssue({ code: 'custom', path: ['hasta'], message: 'Indicá hasta qué mes' });
    if (v.desde && v.hasta && v.desde > v.hasta) {
      ctx.addIssue({ code: 'custom', path: ['hasta'], message: 'Tiene que ser igual o posterior a "desde"' });
    }
  });
export type EncolarArca = z.output<typeof esquemaEncolarArca>;

// ---------------------------------------------------------------- Clave fiscal del usuario

/**
 * Lo que el usuario autoriza al cargar su clave. Se muestra en la pantalla y
 * queda registrado cuándo lo aceptó.
 */
export const TEXTO_CONSENTIMIENTO_ARCA =
  'Autorizo al robot del estudio a ingresar a ARCA con mi CUIT y mi clave fiscal, solo para consultar y '
  + 'descargar datos de los clientes que me delegaron servicios. El robot no presenta, rectifica, paga ni '
  + 'acepta nada. Puedo borrar la clave cuando quiera desde Mi cuenta.';

export const esquemaCredencialArca = z.object({
  cuit: esquemaCuit.refine((c) => tipoPersonaPorCuit(c) === 'humana', 'La clave fiscal es de una persona humana: el CUIT empieza con 20, 23, 24 o 27'),
  // Sin trim: la clave va tal cual la escribió el usuario.
  clave: z.string().min(1, 'Ingresá la clave fiscal').max(64),
  consentimiento: z.literal(true, { error: 'Tenés que aceptar la autorización' }),
  // Código del segundo factor: confirma que es el titular quien carga la clave.
  codigo: z.string().trim().regex(/^\d{6}$/, 'El código tiene 6 dígitos').optional(),
});

// ---------------------------------------------------------------- Acceso

export const esquemaLogin = z.object({
  email: z.string().trim().toLowerCase().max(190),
  password: z.string().max(200),
});

export const esquemaCodigo2fa = z.object({
  codigo: z.string().trim().regex(/^\d{6}$/, 'El código tiene 6 dígitos'),
});

export const esquemaCambioPassword = z
  .object({ actual: z.string().max(200), nueva: esquemaPassword })
  .refine((v) => v.actual !== v.nueva, { path: ['nueva'], message: 'Tiene que ser distinta de la actual' });

// Archivos del cliente

export const esquemaPeriodoArchivo = z.string().regex(/^(19|20)\d{2}-(0[1-9]|1[0-2]|T[1-4])$/, 'Período AAAA-MM');

export const esquemaGrupoCategoria = z.enum(['contable', 'fiscal', 'societario', 'otro']);

/** Clasificar a mano: una categoría existente o una nueva (por nombre). */
export const esquemaClasificarArchivo = z.object({
  categoriaId: z.number().int().positive().nullable().optional(),
  categoriaNueva: z.string().trim().min(2).max(120).optional(),
  periodo: esquemaPeriodoArchivo.nullable().optional(),
}).refine((d) => !(d.categoriaId && d.categoriaNueva), 'Elegí una categoría o escribí una nueva, no las dos');
export type DatosClasificarArchivo = z.infer<typeof esquemaClasificarArchivo>;

export const esquemaCategoria = z.object({
  nombre: z.string().trim().min(2).max(120),
  descripcion: z.string().trim().max(500).nullable().optional(),
  grupo: esquemaGrupoCategoria,
  conciliacion: z.boolean(),
});
export type DatosCategoria = z.infer<typeof esquemaCategoria>;

export const esquemaAsignarMail = z.object({
  clienteId: z.number().int().positive(),
  /** Anotar el remitente para que sus próximos mails entren solos a ese cliente. */
  recordarRemitente: z.boolean().default(false),
});

export const esquemaDocumentosEsperados = z.array(z.object({
  categoriaId: z.number().int().positive(),
  frecuencia: z.enum(['mensual', 'trimestral', 'anual']),
  diaLimite: z.number().int().min(1).max(31),
  observaciones: z.string().trim().max(300).nullable().optional(),
})).max(50);

const listaMails = z.string().trim().max(500).refine(
  (s) => s.split(/[;,\s]+/).filter(Boolean).every((m) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(m)),
  'Mails separados por coma',
);

export const esquemaRecordatorio = z.object({
  habilitado: z.boolean(),
  destinatarios: listaMails,
  diaEnvio: z.number().int().min(1).max(28),
  repetirDias: z.number().int().min(0).max(30),
}).refine((d) => !d.habilitado || d.destinatarios.split(/[;,\s]+/).filter(Boolean).length > 0, {
  message: 'Para habilitarlo hace falta al menos un destinatario', path: ['destinatarios'],
});
